import { Check } from "../lib/icons";

export type MetricReviewQueueItemStatus = "approved" | "revised" | "queued";

export interface MetricReviewQueueItem {
  id: string;
  label: string;
  status: MetricReviewQueueItemStatus;
}

export interface MetricReviewQueueProps {
  items: MetricReviewQueueItem[];
  /** The queued item currently being revised. */
  currentId?: string | null;
  label?: string;
}

function isResolved(status: MetricReviewQueueItemStatus) {
  return status === "approved" || status === "revised";
}

/** Displays progress through a caller-controlled sequence of requested metric revisions. */
export function MetricReviewQueue({
  items,
  currentId = null,
  label = "Revision queue",
}: MetricReviewQueueProps) {
  const remaining = items.filter((item) => !isResolved(item.status)).length;

  return (
    <section
      aria-label={label}
      className="rounded-[10px] border border-(--border-subtle) bg-(--bg-white) p-2.5"
    >
      <div className="mb-2 flex items-center justify-between gap-3">
        <h3 className="text-[10px] font-semibold uppercase tracking-widest text-(--fg-muted)">
          {label}
        </h3>
        <span className="shrink-0 text-[11px] text-(--fg-muted)">
          {remaining === 0 ? "All resolved" : `${remaining} left to revise`}
        </span>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {items.map((item) => {
          const current = item.id === currentId;
          const resolved = isResolved(item.status);
          const statusLabel = current
            ? "Revising"
            : item.status === "approved"
              ? "Approved"
              : item.status === "revised"
                ? "Revised"
                : "Queued";

          return (
            <span
              key={item.id}
              className={[
                "inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[11px] font-medium",
                current
                  ? "border-(--fg-brand) bg-(--bg-sky) text-(--fg-brand)"
                  : resolved
                    ? "border-(--border-default) bg-(--bg-canvas) text-(--fg-secondary)"
                    : "border-(--border-subtle) bg-(--bg-white) text-(--fg-muted)",
              ].join(" ")}
            >
              {resolved ? (
                <Check size={11} />
              ) : current ? (
                <span
                  aria-hidden="true"
                  className="h-1.5 w-1.5 animate-pulse rounded-full bg-(--brand-navy)"
                />
              ) : (
                <span
                  aria-hidden="true"
                  className="h-1.5 w-1.5 rounded-full bg-(--border-strong)"
                />
              )}
              <span className="max-w-40 truncate">{item.label}</span>
              <span className="sr-only">: {statusLabel}</span>
            </span>
          );
        })}
      </div>
    </section>
  );
}
